import { useState, useEffect } from "react";
import axios from "axios";
import {
  TextField,
  Button,
  Typography,
  Alert,
  Box,
  Grid,
  Paper,
  Divider,
  Chip,
  InputAdornment,
  Tooltip,
  IconButton,
  Switch,
  FormControlLabel,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import SaveIcon from "@mui/icons-material/Save";
import EventIcon from "@mui/icons-material/Event";
import TitleIcon from "@mui/icons-material/Title";
import DescriptionIcon from "@mui/icons-material/Description";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import VideocamIcon from "@mui/icons-material/Videocam";
import VpnKeyIcon from "@mui/icons-material/VpnKey";
import LooksOneIcon from "@mui/icons-material/LooksOne";
import LooksTwoIcon from "@mui/icons-material/LooksTwo";
import Looks3Icon from "@mui/icons-material/Looks3";
import Looks4Icon from "@mui/icons-material/Looks4";
import Looks5Icon from "@mui/icons-material/Looks5";
import CloseIcon from "@mui/icons-material/Close";
import InfoIcon from "@mui/icons-material/Info";
import CircularProgress from "@mui/material/CircularProgress";
import Fade from "@mui/material/Fade";
import Backdrop from "@mui/material/Backdrop";
import HistoryIcon from "@mui/icons-material/History";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import RestoreIcon from "@mui/icons-material/Restore";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || "http://localhost:3001";

// 毛玻璃效果的卡片
const GlassCard = styled(Paper)(({ theme }) => ({
  background: "rgba(0, 0, 0, 0.25)",
  backdropFilter: "blur(15px)",
  WebkitBackdropFilter: "blur(15px)",
  borderRadius: "16px",
  padding: theme.spacing(4),
  border: "1px solid rgba(255, 255, 255, 0.2)",
  boxShadow: "0 8px 32px rgba(0, 0, 0, 0.2)",
  height: "100%",
  transition: "all 0.3s ease",
  position: "relative",
  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: "rgba(255, 255, 255, 0.05)",
    borderRadius: 16,
    pointerEvents: "none",
  },
}));

// 毛玻璃效果的输入框
const GlassTextField = styled(TextField)(({ theme }) => ({
  "& .MuiOutlinedInput-root": {
    backgroundColor: "rgba(255, 255, 255, 0.05)",
    borderRadius: "8px",
    "& fieldset": {
      borderColor: "rgba(255, 255, 255, 0.2)",
    },
    "&:hover fieldset": {
      borderColor: "rgba(255, 255, 255, 0.3)",
    },
    "&.Mui-focused fieldset": {
      borderColor: "rgba(255, 255, 255, 0.5)",
    },
  },
  "& .MuiInputLabel-root": {
    color: "rgba(255, 255, 255, 0.8)",
    "&.Mui-focused": {
      color: "rgba(255, 255, 255, 0.95)",
    },
  },
  "& .MuiInputBase-input": {
    color: "rgba(255, 255, 255, 0.95)",
  },
  "& .MuiFormHelperText-root": {
    color: "rgba(255, 255, 255, 0.7)",
    textShadow: "0 1px 2px rgba(0, 0, 0, 0.2)",
  },
  marginBottom: theme.spacing(3),
}));

// 毛玻璃效果的按钮
const GlassButton = styled(Button)(({ theme }) => ({
  background: "rgba(0, 0, 0, 0.6)",
  backdropFilter: "blur(10px)",
  WebkitBackdropFilter: "blur(10px)",
  borderRadius: "8px",
  color: "white",
  padding: theme.spacing(1, 3),
  "&:hover": {
    background: "rgba(0, 0, 0, 0.7)",
  },
  "&:disabled": {
    background: "rgba(0, 0, 0, 0.3)",
    color: "rgba(255, 255, 255, 0.5)",
  },
}));

// 分组标题
const SectionTitle = styled(Typography)(({ theme }) => ({
  fontSize: "1rem",
  fontWeight: 600,
  color: "rgba(255, 255, 255, 0.9)",
  marginBottom: theme.spacing(2),
  marginTop: theme.spacing(2),
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1),
  textShadow: "0 1px 2px rgba(0, 0, 0, 0.2)",
  position: "relative",
  zIndex: 1,
}));

// 卡片选择器
const CardSelector = styled(Box)(({ theme }) => ({
  display: "flex",
  gap: theme.spacing(2),
  marginBottom: theme.spacing(3),
  flexWrap: "wrap",
  "& > *": {
    marginBottom: theme.spacing(2),
  },
}));

const CardOption = styled(Box)(({ theme, selected }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: "80px",
  height: "80px",
  borderRadius: "12px",
  border: selected
    ? "2px solid rgba(255, 255, 255, 0.5)"
    : "1px solid rgba(255, 255, 255, 0.3)",
  backgroundColor: selected
    ? "rgba(255, 255, 255, 0.2)"
    : "rgba(255, 255, 255, 0.1)",
  cursor: "pointer",
  transition: "all 0.2s ease",
}));

// 预览卡片容器 - 增强样式
const PreviewCardContainer = styled(Box)(({ theme }) => ({
  backgroundColor: "rgba(255, 255, 255, 0.05)",
  backdropFilter: "blur(15px)",
  WebkitBackdropFilter: "blur(15px)",
  borderRadius: "12px",
  border: "1px dashed rgba(0, 0, 0, 0.2)",
  padding: theme.spacing(2.5),
  width: "100%",
  maxWidth: "240px",
  minHeight: "170px", // 增加高度
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  boxShadow: "0 4px 20px rgba(0, 0, 0, 0.05)",
  transition: "all 0.3s ease",
}));

// 详情卡片预览容器 - 调整样式与首页卡片一致
const DetailPreviewContainer = styled(Box)(({ theme }) => ({
  backgroundColor: "rgba(255, 255, 255, 0.05)",
  backdropFilter: "blur(15px)",
  WebkitBackdropFilter: "blur(15px)",
  borderRadius: "12px",
  border: "1px dashed rgba(0, 0, 0, 0.2)",
  padding: theme.spacing(2.5),
  width: "100%",
  maxWidth: "240px",
  minHeight: "240px", // 增加高度
  display: "flex",
  flexDirection: "column",
  boxShadow: "0 4px 20px rgba(0, 0, 0, 0.05)",
  transition: "all 0.3s ease",
}));

// 加载中遮罩
const LoadingBackdrop = styled(Backdrop)(({ theme }) => ({
  zIndex: theme.zIndex.drawer + 1,
  backgroundColor: "rgba(255, 255, 255, 0.7)",
  backdropFilter: "blur(4px)",
}));

// 工具按钮容器 - 水平排列
const ToolbarContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1),
  marginRight: "12px", // 推到右侧
  marginLeft: "auto", // 推到右侧
}));

const ToolButton = styled(IconButton)(({ theme }) => ({
  backgroundColor: "rgba(255, 255, 255, 0.1)",
  "&:hover": {
    backgroundColor: "rgba(255, 255, 255, 0.2)",
  },
  padding: theme.spacing(0.8),
}));

// 标题容器 - 统一高度
const TitleContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  marginBottom: theme.spacing(1),
  height: "40px", // 统一高度
}));

const EventsAdmin = () => {
  const [id, setId] = useState("1"); // 默认 ID
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [eventDate, setEventDate] = useState(""); // 日期时间选择器的值
  const [displayTime, setDisplayTime] = useState(""); // 显示用的时间格式
  const [zoomlink, setZoomlink] = useState("");
  const [zoompassword, setZoompassword] = useState("");
  const [error, setError] = useState("");
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [history, setHistory] = useState([]);
  const [copied, setCopied] = useState(false);
  const [isFullscreenLoading, setIsFullscreenLoading] = useState(false);
  const [originalData, setOriginalData] = useState(null);
  const [isRecurring, setIsRecurring] = useState(false); // 是否是定期活动
  const [isManuallyUpdated, setIsManuallyUpdated] = useState(false); // 是否手动更改过

  // 处理卡片选择
  const handleCardSelect = (cardId) => {
    setId(cardId);
    if (
      cardId !== "1" &&
      cardId !== "2" &&
      cardId !== "3" &&
      cardId !== "4" &&
      cardId !== "5"
    ) {
      setError("卡片 ID 输入不正确，请输入 1 到 5");
    } else {
      setError("");
    }
  };

  // 🔹 获取事件数据 - 修改为保存原始数据
  useEffect(() => {
    // 只有当 ID 为 1 到 5 时才获取数据
    if (id === "1" || id === "2" || id === "3" || id === "4" || id === "5") {
      setLoading(true);
      axios
        .get(`${API_BASE_URL}/events/${id}`)
        .then((res) => {
          setTitle(res.data.title || "");
          setDescription(res.data.description || "");

          // 将UTC时间转换为LA时区显示
          const eventTimeUTC = new Date(res.data.time);
          const laTimeZone = "America/Los_Angeles";
          const eventTimeLA = eventTimeUTC.toLocaleString("en-US", {
            timeZone: laTimeZone,
            year: "numeric",
            month: "2-digit",
            day: "2-digit",
            hour: "2-digit",
            minute: "2-digit",
            hour12: false,
          });

          // 转换为datetime-local格式 (YYYY-MM-DDTHH:mm)
          // 格式: "MM/DD/YYYY, HH:mm"
          const [datePart, timePart] = eventTimeLA.split(", ");
          const [month, day, year] = datePart.split("/");
          const localEventDate = `${year}-${month}-${day}T${timePart}`;
          setEventDate(localEventDate);
          setDisplayTime(res.data.displayTime || "");
          setZoomlink(res.data.zoomlink || "");
          setZoompassword(res.data.zoompassword || "");
          setIsRecurring(res.data.isRecurring || false);
          setIsManuallyUpdated(res.data.isManuallyUpdated || false);
          setError("");
          setSelectedEvent(res.data);
          // 保存原始数据用于恢复
          setOriginalData(res.data);
          setLoading(false);
        })
        .catch((error) => {
          console.error("获取事件失败:", error);
          setLoading(false);
          // 如果是 404 错误，说明是新事件
          if (error.response && error.response.status === 404) {
            // 清空表单，准备创建新事件
            setTitle("");
            setDescription("");
            setEventDate("");
            setDisplayTime("");
            setZoomlink("");
            setZoompassword("");
            setIsRecurring(false);
            setIsManuallyUpdated(false);
            setOriginalData(null);
          }
        });
    }
  }, [id]);

  // 复制事件数据为JSON - 增加复制成功提示
  const handleCopyEventData = () => {
    const eventData = {
      id,
      title,
      description,
      time: eventDate,
      displayTime,
      zoomlink,
      zoompassword,
    };

    navigator.clipboard
      .writeText(JSON.stringify(eventData, null, 2))
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      })
      .catch((err) => console.error("复制失败:", err));
  };

  // 恢复数据库原始数据
  const handleRestoreOriginal = () => {
    if (!originalData) return;

    setTitle(originalData.title || "");
    setDescription(originalData.description || "");
    const localEventDate = new Date(originalData.time)
      .toISOString()
      .slice(0, 16);
    setEventDate(localEventDate);
    setDisplayTime(originalData.displayTime || "");
    setZoomlink(originalData.zoomlink || "");
    setZoompassword(originalData.zoompassword || "");
    setIsRecurring(originalData.isRecurring || false);
    setIsManuallyUpdated(originalData.isManuallyUpdated || false);
  };

  // 🔹 处理更新，写入数据库
  const handleSaveEvent = async () => {
    // 验证 ID
    if (id !== "1" && id !== "2" && id !== "3" && id !== "4" && id !== "5") {
      setError("卡片 ID 输入不正确，请输入 1 到 5");
      return;
    }

    // 验证必填字段
    if (!title.trim()) {
      setError("标题不能为空");
      return;
    }

    if (!description.trim()) {
      setError("描述不能为空");
      return;
    }

    if (!eventDate) {
      setError("活动日期时间不能为空");
      return;
    }

    if (!displayTime.trim()) {
      setError("显示时间不能为空");
      return;
    }

    try {
      setLoading(true);
      setIsFullscreenLoading(true); // 显示全屏加载

      // 将用户输入的LA时间转换为UTC时间保存
      // eventDate是datetime-local格式，用户输入的是LA时区时间
      const laTimeZone = "America/Los_Angeles";
      const [datePart, timePart] = eventDate.split("T");
      const [year, month, day] = datePart.split("-");
      const [hours, minutes] = timePart.split(":");

      // 方法：使用一个技巧来正确转换LA时区到UTC
      // 1. 创建一个表示LA时区该时间的UTC时间字符串
      // 2. 使用Intl API来验证和调整

      // 创建一个UTC时间，然后看它在LA时区的显示，反推LA时间对应的UTC时间
      // 先创建一个测试UTC时间（该日期的中午）
      const testUTC = new Date(`${year}-${month}-${day}T12:00:00Z`);
      const testLA = testUTC.toLocaleString("en-US", {
        timeZone: laTimeZone,
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      });

      // 计算偏移：UTC 12:00 在LA时区显示为 testLA
      // 例如：UTC 12:00 在LA显示为 04:00（标准时间）或 05:00（夏令时）
      // 偏移 = 12 - testLA小时
      const [testLAHours] = testLA.split(":");
      const offsetHours = 12 - parseInt(testLAHours);

      // 将用户输入的LA时间转换为UTC
      const inputHours = parseInt(hours);
      const utcHours = inputHours + offsetHours;

      // 创建UTC时间（使用Date对象自动处理跨日情况）
      const baseDate = new Date(`${year}-${month}-${day}T00:00:00Z`);
      baseDate.setUTCHours(utcHours);
      baseDate.setUTCMinutes(parseInt(minutes));
      baseDate.setUTCSeconds(0);
      baseDate.setUTCMilliseconds(0);

      const utcTime = baseDate;

      const eventData = {
        time: utcTime.toISOString(), // 转换为ISO格式的UTC时间
        displayTime,
        title,
        description,
        zoomlink,
        zoompassword,
        isRecurring,
        isManuallyUpdated: true, // 手动保存时标记为已手动更新
      };

      // 如果启用了定期活动，提取星期和时间（使用输入的LA时间）
      if (isRecurring) {
        const laDate = new Date(
          `${year}-${month}-${day}T${hours}:${minutes}:00`
        );
        const dayOfWeek = laDate.getDay(); // 0-6, 0是周日
        eventData.recurringDayOfWeek = dayOfWeek;
        eventData.recurringTime = `${hours}:${minutes}`;
      } else {
        // 如果关闭了定期活动，清除相关字段
        eventData.recurringDayOfWeek = null;
        eventData.recurringTime = null;
        eventData.isManuallyUpdated = false;
      }

      await axios.put(`${API_BASE_URL}/events/${id}`, eventData);

      // 模拟网络延迟
      // await new Promise((resolve) => setTimeout(resolve, 800));

      // 更新成功后，更新原始数据
      const updatedData = {
        title,
        description,
        time: new Date(eventDate).toISOString(),
        displayTime,
        zoomlink,
        zoompassword,
        isRecurring,
        isManuallyUpdated: true,
      };
      setOriginalData(updatedData);
      setIsManuallyUpdated(true);

      setSuccess(true);
      setError("");
      window.scrollTo({ top: 0, behavior: "smooth" });
      setTimeout(() => setSuccess(false), 3000);
    } catch (error) {
      console.error("更新失败:", error);
      setError("更新失败，请检查后端！");
    } finally {
      setLoading(false);
      setIsFullscreenLoading(false); // 隐藏全屏加载
    }
  };

  return (
    <Box sx={{ p: 3, width: "100%" }}>
      {/* 全屏加载指示器 */}
      <LoadingBackdrop open={isFullscreenLoading}>
        <Box sx={{ textAlign: "center" }}>
          <CircularProgress color="inherit" />
          <Typography
            sx={{
              mt: 2,
              color: "rgba(255, 255, 255, 0.85)",
              textShadow: "0 1px 2px rgba(0, 0, 0, 0.2)",
            }}
          >
            保存中，请稍候...
          </Typography>
        </Box>
      </LoadingBackdrop>

      {/* 标题放在外面 */}
      <Typography
        variant="h4"
        sx={{
          mb: 3,
          fontWeight: 500,
          color: "rgba(255, 255, 255, 0.95)",
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          textShadow: "0 1px 2px rgba(0, 0, 0, 0.2)",
        }}
      >
        首页事件管理
      </Typography>

      <Grid container spacing={3}>
        <Grid item xs={12}>
          <GlassCard>
            {/* 提示信息区域 */}
            {(error || success) && (
              <Box sx={{ mb: 4 }}>
                {error && (
                  <Alert
                    severity="error"
                    sx={{
                      backgroundColor: "rgba(253, 237, 237, 0.6)",
                      border: "1px solid rgba(211, 47, 47, 0.1)",
                      borderRadius: "8px",
                    }}
                  >
                    {error}
                  </Alert>
                )}

                {success && (
                  <Alert
                    severity="success"
                    sx={{
                      backgroundColor: "rgba(237, 247, 237, 0.6)",
                      border: "1px solid rgba(46, 125, 50, 0.1)",
                      borderRadius: "8px",
                    }}
                  >
                    更新成功！事件信息已保存。
                  </Alert>
                )}
              </Box>
            )}

            {/* 左右布局 */}
            <Grid container spacing={4}>
              {/* 左侧：选择器和预览 */}
              <Grid item xs={12} md={4} lg={3}>
                <Box sx={{ mb: 4 }}>
                  {/* 统一高度的标题容器 */}
                  <TitleContainer>
                    <Typography
                      variant="subtitle1"
                      sx={{
                        fontWeight: 500,
                        color: "rgba(255, 255, 255, 0.85)",
                        textShadow: "0 1px 2px rgba(0, 0, 0, 0.2)",
                      }}
                    >
                      选择要编辑的卡片
                    </Typography>
                  </TitleContainer>

                  <CardSelector>
                    <CardOption
                      selected={id === "1"}
                      onClick={() => handleCardSelect("1")}
                    >
                      <LooksOneIcon
                        sx={{
                          fontSize: 40,
                          color:
                            id === "1"
                              ? "rgba(255, 255, 255, 0.95)"
                              : "rgba(255, 255, 255, 0.7)",
                        }}
                      />
                    </CardOption>
                    <CardOption
                      selected={id === "2"}
                      onClick={() => handleCardSelect("2")}
                    >
                      <LooksTwoIcon
                        sx={{
                          fontSize: 40,
                          color:
                            id === "2"
                              ? "rgba(0, 0, 0, 0.8)"
                              : "rgba(0, 0, 0, 0.5)",
                        }}
                      />
                    </CardOption>
                    <CardOption
                      selected={id === "3"}
                      onClick={() => handleCardSelect("3")}
                    >
                      <Looks3Icon
                        sx={{
                          fontSize: 40,
                          color:
                            id === "3"
                              ? "rgba(0, 0, 0, 0.8)"
                              : "rgba(0, 0, 0, 0.5)",
                        }}
                      />
                    </CardOption>
                    <CardOption
                      selected={id === "4"}
                      onClick={() => handleCardSelect("4")}
                    >
                      <Looks4Icon
                        sx={{
                          fontSize: 40,
                          color:
                            id === "4"
                              ? "rgba(0, 0, 0, 0.8)"
                              : "rgba(0, 0, 0, 0.5)",
                        }}
                      />
                    </CardOption>
                    <CardOption
                      selected={id === "5"}
                      onClick={() => handleCardSelect("5")}
                    >
                      <Looks5Icon
                        sx={{
                          fontSize: 40,
                          color:
                            id === "5"
                              ? "rgba(0, 0, 0, 0.8)"
                              : "rgba(0, 0, 0, 0.5)",
                        }}
                      />
                    </CardOption>
                  </CardSelector>
                </Box>

                {/* 首页卡片预览 */}
                <Box sx={{ mb: 3 }}>
                  {/* 标题和工具箱在同一行 - 使用统一高度的容器 */}
                  <TitleContainer>
                    <Typography
                      variant="subtitle1"
                      sx={{
                        fontWeight: 500,
                        color: "rgba(255, 255, 255, 0.85)",
                        textShadow: "0 1px 2px rgba(0, 0, 0, 0.2)",
                      }}
                    >
                      <InfoOutlinedIcon
                        fontSize="small"
                        sx={{
                          mr: 1,
                          verticalAlign: "middle",
                          color: "rgba(255, 255, 255, 0.8)",
                        }}
                      />
                      首页卡片预览
                    </Typography>

                    {/* 工具箱 - 水平排列在标题右侧 */}
                    <ToolbarContainer>
                      <Tooltip title={copied ? "已复制！" : "复制为JSON"}>
                        <Box sx={{ position: "relative" }}>
                          <ToolButton
                            onClick={handleCopyEventData}
                            color={copied ? "success" : "default"}
                            size="small"
                          >
                            <ContentCopyIcon fontSize="small" />
                          </ToolButton>
                          {copied && (
                            <CheckCircleIcon
                              color="success"
                              sx={{
                                position: "absolute",
                                top: -5,
                                right: -5,
                                fontSize: 14,
                                backgroundColor: "white",
                                borderRadius: "50%",
                              }}
                            />
                          )}
                        </Box>
                      </Tooltip>

                      <Tooltip title="恢复数据库原始数据">
                        <span>
                          <ToolButton
                            onClick={handleRestoreOriginal}
                            disabled={!originalData}
                            size="small"
                          >
                            <RestoreIcon fontSize="small" />
                          </ToolButton>
                        </span>
                      </Tooltip>
                    </ToolbarContainer>
                  </TitleContainer>

                  <PreviewCardContainer>
                    {title ? (
                      <>
                        <Typography
                          variant="h6"
                          sx={{
                            fontWeight: 600,
                            mb: 1,
                            color: "rgba(255, 255, 255, 0.95)",
                            textShadow: "0 1px 2px rgba(0, 0, 0, 0.2)",
                            fontSize: "1rem",
                          }}
                        >
                          {title}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            mb: 2,
                            color: "rgba(255, 255, 255, 0.8)",
                            fontSize: "0.85rem",
                            lineHeight: 1.4,
                          }}
                        >
                          {description}
                        </Typography>
                        {displayTime && (
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                              mt: "auto", // 推到底部
                              pt: 1,
                              borderTop: "1px dashed rgba(0, 0, 0, 0.1)",
                            }}
                          >
                            <AccessTimeIcon
                              fontSize="small"
                              sx={{ color: "rgba(255, 255, 255, 0.8)" }}
                            />
                            <Typography
                              variant="body2"
                              sx={{
                                color: "rgba(255, 255, 255, 0.85)",
                                textShadow: "0 1px 2px rgba(0, 0, 0, 0.2)",
                              }}
                            >
                              {displayTime}
                            </Typography>
                          </Box>
                        )}
                      </>
                    ) : (
                      <Typography
                        variant="body1"
                        sx={{
                          textAlign: "center",
                          color: "rgba(255, 255, 255, 0.8)",
                        }}
                      >
                        填写表单后这里将显示卡片预览
                      </Typography>
                    )}
                  </PreviewCardContainer>
                </Box>

                {/* 详情卡片预览 */}
                <Box sx={{ mb: 3 }}>
                  {/* 使用统一高度的标题容器 */}
                  <TitleContainer>
                    <Typography
                      variant="subtitle1"
                      sx={{
                        fontWeight: 500,
                        color: "rgba(255, 255, 255, 0.85)",
                        textShadow: "0 1px 2px rgba(0, 0, 0, 0.2)",
                      }}
                    >
                      <InfoIcon
                        fontSize="small"
                        sx={{
                          mr: 1,
                          verticalAlign: "middle",
                          color: "rgba(255, 255, 255, 0.8)",
                        }}
                      />
                      详情卡片预览
                    </Typography>
                  </TitleContainer>

                  <DetailPreviewContainer>
                    {title ? (
                      <>
                        {/* 标题栏 */}
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            mb: 2,
                            pb: 1,
                            borderBottom: "1px dashed rgba(0, 0, 0, 0.1)",
                          }}
                        >
                          <Typography
                            variant="h6"
                            sx={{
                              fontWeight: 600,
                              color: "rgba(255, 255, 255, 0.95)",
                              textShadow: "0 1px 2px rgba(0, 0, 0, 0.2)",
                              fontSize: "1rem",
                            }}
                          >
                            {title}
                          </Typography>
                          <CloseIcon
                            sx={{
                              fontSize: 18,
                              color: "rgba(255, 255, 255, 0.8)",
                            }}
                          />
                        </Box>

                        {/* 活动时间 */}
                        <Box
                          sx={{
                            mb: 2,
                            display: "flex",
                            alignItems: "center",
                          }}
                        >
                          <AccessTimeIcon
                            sx={{
                              color: "rgba(255, 255, 255, 0.8)",
                              mr: 1,
                              fontSize: "1rem",
                            }}
                          />
                          <Box>
                            <Typography
                              sx={{
                                fontWeight: 600,
                                color: "rgba(255, 255, 255, 0.85)",
                                fontSize: "0.85rem",
                                mb: 0.2,
                                textShadow: "0 1px 2px rgba(0, 0, 0, 0.2)",
                              }}
                            >
                              活动时间
                            </Typography>

                            <Typography
                              sx={{
                                color: "rgba(255, 255, 255, 0.95)",
                                textShadow: "0 1px 2px rgba(0, 0, 0, 0.2)",
                                fontWeight: 600,
                                fontSize: "1rem",
                              }}
                            >
                              {displayTime}
                            </Typography>
                          </Box>
                        </Box>

                        {/* 活动详情 */}
                        <Box sx={{ mb: 2 }}>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              mb: 0.5,
                            }}
                          >
                            <InfoIcon
                              sx={{
                                color: "rgba(255, 255, 255, 0.8)",
                                mr: 1,
                                fontSize: "1rem",
                              }}
                            />
                            <Typography
                              sx={{
                                fontWeight: 600,
                                color: "rgba(255, 255, 255, 0.85)",
                                fontSize: "0.85rem",
                                textShadow: "0 1px 2px rgba(0, 0, 0, 0.2)",
                              }}
                            >
                              活动详情
                            </Typography>
                          </Box>

                          <Typography
                            sx={{
                              ml: 0.5,
                              color: "rgba(255, 255, 255, 0.85)",
                              fontSize: "0.9rem",
                              lineHeight: 1.5,
                              maxHeight: "60px",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              display: "-webkit-box",
                              WebkitLineClamp: 3,
                              WebkitBoxOrient: "vertical",
                              textShadow: "0 1px 2px rgba(0, 0, 0, 0.2)",
                            }}
                          >
                            {description}
                          </Typography>
                        </Box>

                        {/* Zoom信息 */}
                        {(zoomlink || zoompassword) && (
                          <Box
                            sx={{
                              p: 1.5,
                              borderRadius: "8px",
                              border: "1px dashed rgba(0, 0, 0, 0.2)",
                              backgroundColor: "rgba(255, 255, 255, 0.2)",
                              mt: "auto", // 推到底部
                            }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                mb: 0.5,
                              }}
                            >
                              <VideocamIcon
                                sx={{
                                  color: "rgba(255, 255, 255, 0.8)",
                                  mr: 1,
                                  fontSize: "1rem",
                                }}
                              />
                              <Typography
                                sx={{
                                  fontWeight: 600,
                                  color: "rgba(255, 255, 255, 0.85)",
                                  fontSize: "0.85rem",
                                  textShadow: "0 1px 2px rgba(0, 0, 0, 0.2)",
                                }}
                              >
                                Zoom会议
                              </Typography>
                            </Box>
                            {zoomlink && (
                              <Typography
                                sx={{
                                  ml: 0.5,
                                  color: "rgba(255, 255, 255, 0.85)",
                                  fontSize: "0.85rem",
                                  mb: 0.5,
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                  textShadow: "0 1px 2px rgba(0, 0, 0, 0.2)",
                                }}
                              >
                                链接: {zoomlink}
                              </Typography>
                            )}
                            {zoompassword && (
                              <Typography
                                sx={{
                                  ml: 0.5,
                                  color: "rgba(255, 255, 255, 0.85)",
                                  fontSize: "0.85rem",
                                  textShadow: "0 1px 2px rgba(0, 0, 0, 0.2)",
                                }}
                              >
                                密码: {zoompassword}
                              </Typography>
                            )}
                          </Box>
                        )}
                      </>
                    ) : (
                      <Typography
                        variant="body1"
                        sx={{
                          textAlign: "center",
                          color: "rgba(255, 255, 255, 0.8)",
                        }}
                      >
                        填写表单后这里将显示详情卡片预览
                      </Typography>
                    )}
                  </DetailPreviewContainer>
                </Box>
              </Grid>

              {/* 右侧：表单 */}
              <Grid item xs={12} md={8} lg={9}>
                {/* 基本信息 - 使用统一高度的标题容器 */}
                <TitleContainer>
                  <SectionTitle>
                    <TitleIcon />
                    基本信息
                  </SectionTitle>
                </TitleContainer>

                <GlassTextField
                  label="活动标题"
                  type="text"
                  fullWidth
                  variant="outlined"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  error={error === "标题不能为空"}
                  helperText={error === "标题不能为空" ? error : ""}
                  required
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <TitleIcon sx={{ color: "rgba(255, 255, 255, 0.8)" }} />
                      </InputAdornment>
                    ),
                  }}
                />

                <GlassTextField
                  label="活动描述"
                  type="text"
                  fullWidth
                  variant="outlined"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  error={error === "描述不能为空"}
                  helperText={error === "描述不能为空" ? error : ""}
                  required
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <DescriptionIcon
                          sx={{ color: "rgba(255, 255, 255, 0.8)" }}
                        />
                      </InputAdornment>
                    ),
                  }}
                />

                {/* 添加字数统计 */}
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "flex-end",
                    mt: 1,
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      color:
                        description.length > 100
                          ? "rgba(255, 100, 100, 0.9)"
                          : "rgba(255, 255, 255, 0.7)",
                      textShadow: "0 1px 2px rgba(0, 0, 0, 0.2)",
                    }}
                  >
                    {description.length}/100 字符
                  </Typography>
                </Box>

                {/* 时间信息 - 使用统一高度的标题容器 */}
                <TitleContainer>
                  <SectionTitle>
                    <AccessTimeIcon />
                    时间信息
                  </SectionTitle>
                </TitleContainer>

                <GlassTextField
                  label="活动日期时间"
                  type="datetime-local"
                  fullWidth
                  variant="outlined"
                  value={eventDate}
                  onChange={(e) => {
                    setEventDate(e.target.value);
                    // 用户手动更改时间时，标记为手动更新
                    if (isRecurring) {
                      setIsManuallyUpdated(true);
                    }
                  }}
                  error={error === "活动日期时间不能为空"}
                  helperText={
                    error === "活动日期时间不能为空" ? error : "用于计算倒计时"
                  }
                  InputLabelProps={{ shrink: true }}
                  required
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <EventIcon sx={{ color: "rgba(255, 255, 255, 0.8)" }} />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <Tooltip title="此时间用于计算倒计时，请使用您所在时区的时间">
                          <IconButton size="small">
                            <InfoOutlinedIcon
                              fontSize="small"
                              sx={{ color: "rgba(255, 255, 255, 0.8)" }}
                            />
                          </IconButton>
                        </Tooltip>
                      </InputAdornment>
                    ),
                  }}
                />

                <GlassTextField
                  label="显示时间"
                  type="text"
                  fullWidth
                  variant="outlined"
                  value={displayTime}
                  onChange={(e) => setDisplayTime(e.target.value)}
                  error={error === "显示时间不能为空"}
                  helperText={
                    error === "显示时间不能为空" ? error : "例如: 周日 10:00"
                  }
                  required
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <AccessTimeIcon
                          sx={{ color: "rgba(255, 255, 255, 0.8)" }}
                        />
                      </InputAdornment>
                    ),
                  }}
                />

                {/* 自动更新开关 */}
                <Box sx={{ mb: 3 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={isRecurring}
                        onChange={(e) => {
                          setIsRecurring(e.target.checked);
                          // 如果关闭自动更新，清除手动更新标记
                          if (!e.target.checked) {
                            setIsManuallyUpdated(false);
                          }
                        }}
                        sx={{
                          "& .MuiSwitch-switchBase.Mui-checked": {
                            color: "#fff",
                          },
                          "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track":
                            {
                              backgroundColor: "rgba(255, 255, 255, 0.5)",
                            },
                        }}
                      />
                    }
                    label={
                      <Typography
                        sx={{
                          color: "rgba(255, 255, 255, 0.9)",
                          fontSize: "0.9rem",
                          textShadow: "0 1px 2px rgba(0, 0, 0, 0.2)",
                        }}
                      >
                        启用自动更新（每周自动更新到下周同一时间）
                      </Typography>
                    }
                  />
                  {isRecurring && (
                    <Typography
                      sx={{
                        mt: 1,
                        ml: 4.5,
                        color: "rgba(255, 255, 255, 0.7)",
                        fontSize: "0.85rem",
                        textShadow: "0 1px 2px rgba(0, 0, 0, 0.2)",
                      }}
                    >
                      活动时间过期后，将自动更新到下周同一时间
                    </Typography>
                  )}
                </Box>

                {/* Zoom会议信息 - 使用统一高度的标题容器 */}
                <TitleContainer>
                  <SectionTitle>
                    <VideocamIcon />
                    Zoom会议信息
                  </SectionTitle>
                </TitleContainer>

                <GlassTextField
                  label="Zoom 会议链接"
                  type="text"
                  fullWidth
                  variant="outlined"
                  value={zoomlink}
                  onChange={(e) => setZoomlink(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <VideocamIcon
                          sx={{ color: "rgba(255, 255, 255, 0.8)" }}
                        />
                      </InputAdornment>
                    ),
                  }}
                />

                <GlassTextField
                  label="Zoom 密码"
                  type="text"
                  fullWidth
                  variant="outlined"
                  value={zoompassword}
                  onChange={(e) => setZoompassword(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <VpnKeyIcon
                          sx={{ color: "rgba(255, 255, 255, 0.8)" }}
                        />
                      </InputAdornment>
                    ),
                  }}
                />

                {/* 提交按钮 - 放在Zoom表单下方 */}
                <Box
                  sx={{ display: "flex", justifyContent: "flex-end", mt: 3 }}
                >
                  {success && (
                    <Alert
                      severity="success"
                      sx={{
                        backgroundColor: "rgba(237, 247, 237, 0.6)",
                        border: "1px solid rgba(46, 125, 50, 0.1)",
                        borderRadius: "8px",
                        flexGrow: 1,
                        mr: 2,
                      }}
                    >
                      更新成功！事件信息已保存。
                    </Alert>
                  )}

                  <GlassButton
                    variant="contained"
                    onClick={handleSaveEvent}
                    disabled={
                      (id !== "1" &&
                        id !== "2" &&
                        id !== "3" &&
                        id !== "4" &&
                        id !== "5") ||
                      loading
                    }
                    startIcon={<SaveIcon />}
                    sx={{ px: 4, py: 1.2 }}
                  >
                    {loading ? "保存中..." : "保存修改"}
                  </GlassButton>
                </Box>
              </Grid>
            </Grid>
          </GlassCard>
        </Grid>
      </Grid>
    </Box>
  );
};

export default EventsAdmin;
